import ButtonLink from "core/components/ButtonLink";
import { Row } from "core/styles";
import styled from "styled-components";
import { Outlet, useNavigate } from "react-router";

import useCurrentUser from "core/hooks/useCurrentUser";
import { useMemo } from "react";
import {
  REVERSE_LOOKUP_TABLEAU_ROLES,
  PERMISSIONS,
  FLAG_KEYS,
} from "core/constants";
import Typography from "core/components/Typography";
import { useOrganizationByIdQuery } from "core/queries";
import useActiveMembership from "core/hooks/useActiveMembership";
import useHasPermissions from "core/hooks/useHasPermissions";
import { useCheckFeatureFlag } from "core/hooks/featureFlagging";
import { hasAnalyticsHubUserAccess } from "common/utilities";

import Pill from "core/components/Pill";
import Flex from "core/components/Flex";
import { ANALYTICS_HUB_DEFAULT_SEARCH } from "modules/analyticsHub/constants";

const Container = styled.div`
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;

  padding-top: 60px;
  padding-bottom: 20px;
  padding-left: 50px;
  padding-right: 50px;

  @media only screen and (max-width: 1400px) {
  }

  @media only screen and (min-width: 2000px) {
  }
`;

const HomeColumns = styled.div`
  display: flex;
  gap: 36px;
  /* flex-wrap: wrap;
  max-width: 1400px;
  align-items: flex-start; */
  flex-direction: column;

  /* @media only screen and (max-width: 1400px) {
    max-width: 700px;
    flex-direction: column;
  }

  @media only screen and (min-width: 2000px) {
    max-width: 100%;
    margin-top: 20vh;
  } */
`;

const TitleColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  color: var(--text-header);

  /* @media only screen and (max-width: 1400px) {
    margin-top: 0px;
    flex: 0 1 auto;
  }

  @media only screen and (min-width: 2000px) {
    margin-top: 0px; */
  }
`;

export const CardColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  max-width: 875px;
`;

const Card = styled(Row)`
  align-items: flex-start;
  width: 100%;
  max-width: 100%;

  /* height: fit-content; */
  height: 205px;

  padding: 24px;
  border-radius: 5px;
  background-color: var(--color-base-100, #ffffff);
  border: 1px solid #979797;
  box-sizing: border-box;
`;

const CardImageContainer = styled.div`
  height: 100%;
  width: 160px;
  flex: 1 0 160px;

  display: flex;
  justify-content: center;

  img {
    display: block;
    height: 100%;
  }
`;

const StyledHeaderPrimary = styled(Typography)`
  color: var(--text-header, #000000);
  font-size: 60px;
  line-height: 60px;
  font-weight: 600;
`;

const StyledHeaderSecondary = styled(Typography)`
  color: var(--text-header, #000000);
  font-size: 28px;
  line-height: 36px;
  font-weight: 600;
`;

const Home = () => {
  const { activeMembership } = useActiveMembership();

  const hasAnalyticsHubPermission = useHasPermissions(
    PERMISSIONS[activeMembership.role],
    "analytics-hub:read"
  );
  const hasAnalyticsHubFeature = useCheckFeatureFlag(FLAG_KEYS.ANALYTICS_HUB);
  const hasAnalyticsHubAccessLevel =
    hasAnalyticsHubUserAccess(activeMembership);

  const { data: organization } = useOrganizationByIdQuery({
    organizationId: activeMembership.id,
    include: "licenses,onboard",
  });

  const hasBiPortalLicense = useMemo(
    () =>
      Object.values(REVERSE_LOOKUP_TABLEAU_ROLES).includes(
        activeMembership?.tableau_config?.license
      ),
    [activeMembership]
  );

  const biPortalUrl = useMemo(
    () => organization?.onboarding?.bi?.portal_url,
    [organization]
  );

  const biPortal = useMemo(
    () =>
      organization // user and org has loaded
        ? hasBiPortalLicense && biPortalUrl
          ? {
              // valid bi portal link and user license
              disabled: false,
              errorMessage: null,
            }
          : {
              // invalid
              disabled: true,
              errorMessage: !hasBiPortalLicense
                ? "You do not have a BI Portal license assigned." // no BI licence for user
                : "The BI Portal is not set up for your organization.", // no BI portal link for org
            }
        : {
            // user and/or org not loaded yet, disable button
            disabled: true,
            errorMessage: null,
            isLoading: true,
          },
    [organization, hasBiPortalLicense, biPortalUrl]
  );

  return (
    <Container>
      <HomeColumns>
        <TitleColumn>
          <StyledHeaderSecondary
            variant="h4"
            noMargin
            data-test="welcome-header"
          >
            Welcome to Definitive Healthcare
          </StyledHeaderSecondary>
          <StyledHeaderPrimary variant="h1" noMargin data-test="product-name">
            Populi Suite
          </StyledHeaderPrimary>
        </TitleColumn>

        <CardColumn>
          {hasAnalyticsHubFeature &&
            hasAnalyticsHubPermission &&
            hasAnalyticsHubAccessLevel && (
              <Card data-test="analytics-hub-card">
                <CardImageContainer>
                  <img src="/analytics-hub-graphic.png" alt="Analytics Hub" />
                </CardImageContainer>
                <div>
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    gap="8px"
                    style={{ marginBottom: "12px" }}
                  >
                    <Typography variant="h4" noMargin>
                      Analytics Hub
                    </Typography>
                    <Pill size="large" variant="success">
                      New Feature
                    </Pill>
                  </Flex>
                  <Typography variant="p">
                    Introducing the Analytics Hub: a streamlined,
                    high-performance embedded analytics experience within the
                    Populi Suite.
                  </Typography>

                  <Row style={{ gap: "5px" }}>
                    <ButtonLink
                      to={`/analytics-hub${ANALYTICS_HUB_DEFAULT_SEARCH}`}
                    >
                      Start Exploring
                    </ButtonLink>
                  </Row>
                </div>
              </Card>
            )}

          <Card data-test="bi-portal-card">
            <CardImageContainer>
              <img src="/bi-portal-graphic.png" alt="BI Portal" />
            </CardImageContainer>
            <div>
              <Typography variant="h4" style={{ marginBottom: "12px" }}>
                Legacy BI Portal
              </Typography>
              <Typography variant="p">
                Ready-to-use dashboards for Market, Provider, and Population
                Intelligence, based on your organization&apos;s subscription.
              </Typography>

              <Row style={{ gap: "5px" }}>
                <ButtonLink
                  href={biPortal.disabled ? null : biPortalUrl}
                  disabled={biPortal.disabled}
                  isLoading={biPortal.isLoading}
                >
                  Launch Portal
                </ButtonLink>
                {biPortal.errorMessage ? (
                  <Typography variant="sub-text" noMargin>
                    {biPortal.errorMessage}
                  </Typography>
                ) : null}
              </Row>
            </div>
          </Card>
          <Card data-test="success-portal-card">
            <CardImageContainer>
              <img src="/support-graphic.png" alt="Support" />
            </CardImageContainer>
            <div>
              <Typography variant="h4" style={{ marginBottom: "12px" }}>
                Success Portal
              </Typography>
              <Typography variant="p">
                Valuable end-user documentation, including data dictionaries,
                product overviews, training content, support articles, and
                release notes. You can also use the Support Portal to submit
                questions or issues.
              </Typography>
              <ButtonLink href="https://success.populi.ai/hc/en-us">
                Get Support
              </ButtonLink>
            </div>
          </Card>
        </CardColumn>
      </HomeColumns>

      <Outlet />
    </Container>
  );
};

export default Home;
